import { defineStore } from "pinia";
import { state } from "./state";
import { getters } from "./getters";
// 使用命名导入代替通配符导入，避免潜在冲突
import {
  // 初始化相关
  initialize,
  initializeExperience,
  fetchProducts,
  restoreConnectionsAfterRefresh,
  initStore,
  setupBackgroundPlaySupport,
  handleProductDeleted, // 新增：处理产品删除
  // 持久化相关
  saveStateToStorage,
  loadStateFromStorage,
  // 连接相关
  checkConnectionStatus,
  disconnectRoom,
  // 音频相关 - 基于Howler.js
  playNext,
  onPlayError,
  advanceToNext,
  resumePlayWithUserInteraction,
  setupMediaSession,
  cleanupAllTimers,
  leaveRoom,
  pauseRoom, // 新增：暂停房间但保留播放器
  resumeRoom, // 新增：恢复房间播放器
  enterRoom,
  // 新增：缺失的音频相关函数
  forceCleanupRoom,
  // 新增：执行待切换的产品
  executePendingProductSwitch,
  executePendingProductSwitchWeb,
  // 音频激活器相关
  startAutoplayActivator,
  processActivationQueue,
  updateMediaEngagement,
  requestUserActivation,
  // API相关
  saveConfig,
  startLive,
  startExperienceLive,
  endExperienceLive,
  endLive,
  getExternalRoomId,
  mockAsk,
  sendMessage,
  // UI相关
  setLiveRoomRef,
  setPublicInfoRef,
  setQueueRef,
  startEdit,
  stopEdit,
  scrollToBottom,
  handleScroll,
  resetState,
  muteAllRoomsOnPageLeave,
  forcemuteAllRooms,
  setPreviewAudioStatus,
  updateAudioStateAfterActivation,
  setupPageChangeListener,
  setupGlobalInteractionListener,
  setupDouYinStyleAutoplay,
  // 移除未使用的函数：showInteractionOverlay
  // 移除权限相关函数，不再需要
  // 音色相关
  setVoicelang,
  syncVoiceToPage,
  fetchInitialVoiceData,
  findAndSetVoice,
  fetchVoiceList,
  onGenderTabClick,
  handleChangeLaugsTabs,
  selectVoice,
  handleVoicePageChange,
  // 商品相关
  selectProduct,
  selectProductWeb,
  openProductSelector,
  fetchProductsForSelector,
  handleProductConfirm,
  handleProductCancel,
  updateSelectedProducts,
  closeProductSelector,
  // WebSocket相关
  toggleBind,
  fetchSessionId,
  bindCommentWebSocket,
  sendBindMessage,
  unbindCommentWebSocket,
  startCommentWsHeartbeat,
  stopCommentWsHeartbeat,
  initAudioQueueWebSocket,
  leaveRoomWebSocket,
  enterRoomWebSocket,
  // 新增：队列适配器
  initHighPerformanceQueue,
  addAudioToQueue,
  getNextAudioFromQueue,
  syncQueueForUI,
  getQueueLength,
  clearQueue,
  getQueueStats,
  hasUrgentItems,
  updateQueueItem,
  updateQueueItemById,
  addToHowlerPlaylist,
  // 同步相关
  initLiveRoomSync,
  handleQueueSync,
  handlePublicMessageSync,
  handleRoomStatusSync,
  handleRoomFullSync,
  sendQueueStatus,
  sendPublicMessageStatus,
  sendRoomStatus,
  requestRoomSync,
  cleanupLiveRoomSync,
  syncCurrentRoomStatus,
  setupQueueAutoSync,
  setupPublicMessageAutoSync,
  // 跨浏览器同步相关
  createCrossBrowserSyncActions,
  // 轮播局部持久化
  persistCarouselForCurrentRoom,
  restoreCarouselForCurrentRoom,
  initExperienceAskWebSocket,
  destroyUniversalWebAudio,
  clearAudioQueueAfterCurrent,
  handleSearchVoice,
  fetchLanguageList
} from "./actions";
import { reconcileSubtitleWithAudible } from "./actions/consistency";

export const useLiveRoomStore = defineStore("liveRoom", {
  state,
  getters,
  actions: {
    // 初始化相关
    initialize,
    initializeExperience,
    fetchProducts,
    restoreConnectionsAfterRefresh,
    initStore,
    setupBackgroundPlaySupport,
    handleProductDeleted, // 新增：处理产品删除
    // 持久化相关
    saveStateToStorage,
    loadStateFromStorage,
    // 连接相关
    checkConnectionStatus,
    disconnectRoom,
    // 音频相关
    playNext,
    onPlayError,
    advanceToNext,
    resumePlayWithUserInteraction,
    setupMediaSession,
    cleanupAllTimers,
    leaveRoom,
    pauseRoom, // 新增：暂停房间但保留播放器
    resumeRoom, // 新增：恢复房间播放器
    enterRoom,
    // 新增：缺失的音频相关函数
    forceCleanupRoom,
    // 音频激活器相关
    startAutoplayActivator,
    processActivationQueue,
    updateMediaEngagement,
    requestUserActivation,
    // API相关
    saveConfig,
    startLive,
    startExperienceLive,
    endExperienceLive,
    endLive,
    getExternalRoomId,
    mockAsk,
    sendMessage,
    // UI相关
    setLiveRoomRef,
    setPublicInfoRef,
    setQueueRef,
    startEdit,
    stopEdit,
    scrollToBottom,
    handleScroll,
    resetState,
    muteAllRoomsOnPageLeave,
    forcemuteAllRooms,
    updateAudioStateAfterActivation,
    setupPageChangeListener,
    setupGlobalInteractionListener,
    setupDouYinStyleAutoplay,
    // 移除未使用的函数：showInteractionOverlay
    // 移除权限相关函数，不再需要
    // 音色相关
    setVoicelang,
    syncVoiceToPage,
    fetchInitialVoiceData,
    findAndSetVoice,
    fetchVoiceList,
    onGenderTabClick,
    handleChangeLaugsTabs,
    selectVoice,
    handleVoicePageChange,
    // 商品相关
    selectProduct,
    selectProductWeb,
    openProductSelector,
    fetchProductsForSelector,
    handleProductConfirm,
    handleProductCancel,
    updateSelectedProducts,
    closeProductSelector,
    // WebSocket相关
    toggleBind,
    fetchSessionId,
    bindCommentWebSocket,
    sendBindMessage,
    unbindCommentWebSocket,
    startCommentWsHeartbeat,
    stopCommentWsHeartbeat,
    initAudioQueueWebSocket,
    leaveRoomWebSocket,
    enterRoomWebSocket,
    // 新增：队列适配器方法
    initHighPerformanceQueue,
    addAudioToQueue,
    getNextAudioFromQueue,
    syncQueueForUI,
    getQueueLength,
    clearQueue,
    getQueueStats,
    hasUrgentItems,
    updateQueueItem,
    updateQueueItemById,
    // 新增：执行待切换的产品
    executePendingProductSwitch,
    executePendingProductSwitchWeb,
    // 同步相关
    initLiveRoomSync,
    handleQueueSync,
    handlePublicMessageSync,
    handleRoomStatusSync,
    handleRoomFullSync,
    sendQueueStatus,
    sendPublicMessageStatus,
    sendRoomStatus,
    requestRoomSync,
    cleanupLiveRoomSync,
    syncCurrentRoomStatus,
    setupQueueAutoSync,
    setupPublicMessageAutoSync,
    // 跨浏览器同步相关
    setupCrossBrowserSync() {
      const syncActions = createCrossBrowserSyncActions(this);
      // 将方法绑定到store实例上
      Object.assign(this, syncActions);
      return syncActions.setupCrossBrowserSync();
    },
    // 轮播局部持久化
    persistCarouselForCurrentRoom,
    restoreCarouselForCurrentRoom,
    // 字幕-音频一致性：以实际在播ID为准强制对齐
    reconcileSubtitleWithAudible,
    cleanupCrossBrowserSync() {
      const syncActions = createCrossBrowserSyncActions(this);
      return syncActions.cleanupCrossBrowserSync();
    },
    initLiveConnections() {
      const syncActions = createCrossBrowserSyncActions(this);
      return syncActions.initLiveConnections();
    },
    cleanupLiveConnections() {
      const syncActions = createCrossBrowserSyncActions(this);
      return syncActions.cleanupLiveConnections();
    },
    initExperienceAskWebSocket,
    destroyUniversalWebAudio,
    clearAudioQueueAfterCurrent,
    handleSearchVoice,
    fetchLanguageList
    // 临时移除移动端专用函数，避免PC端调用冲突
    // playNextMobile,
    // leaveRoomMobile,
    // enterRoomMobile,
    // addToHowlerPlaylist
  }
});
